import {
  trackFeatureUsage,
  trackFlowComplete,
  trackFlowStep,
  type ConversionSurface,
} from "@/utils/gtm";
import type { RailStepId } from "./types";

/**
 * This run's identity in the shared flow_step / flow_complete events.
 *
 * One pair of events covers both guided runs; flow_name is what separates the
 * logged-in onboarding rail from the logged-out demo.
 */
const ONBOARDING_FLOW_NAME = "onboarding";
const ONBOARDING_SURFACE: ConversionSurface = "onboarding_rail";

/**
 * Two tiers, the same split the demo guide uses.
 *
 * The rail's progress — each step, and the run finishing — goes out as the
 * shared flow_step / flow_complete events, because that is the series anyone
 * actually charts and it should not have to be reassembled from a generic
 * event's free-text detail field. Everything AROUND the run — the welcome flow,
 * the checklist, exits, resumes — stays on feature_usage.
 */
export type OnboardingAnalyticsAction =
  | "started"
  | "step_completed"
  | "skipped"
  | "resumed"
  | "template_applied"
  | "watchlists_selected"
  | "concierge_requested"
  | "concierge_applied"
  | "checklist_opened"
  | "checklist_task_done"
  | "checklist_dismissed"
  // A skipped step is not reported here. It rides on flow_step with
  // `skipped: true`, so the step stream stays unbroken and a funnel can be read
  // from one series — see trackFlowStep.
  | "rail_exited"
  | "rail_resumed";

/**
 * Onboarding funnel events, routed through the existing GA4 feature_usage
 * event so they land in the same GTM container with user props attached.
 */
export function trackOnboarding(
  action: OnboardingAnalyticsAction,
  detail?: string
) {
  trackFeatureUsage({
    feature_name: "onboarding",
    feature_action: action,
    ...(detail && { feature_detail: detail }),
  });
}

/**
 * One rail step reached its end, however it ended.
 *
 * Emitted for skipped steps too — see trackFlowStep for why the step stream has
 * to be unbroken for a funnel to be readable.
 */
export function trackRailStep(params: {
  stepId: RailStepId;
  /** Zero-based position in the run; converted to 1-based for the event. */
  stepIndex: number;
  stepTotal: number;
  skipped: boolean;
}) {
  trackFlowStep({
    flow_name: ONBOARDING_FLOW_NAME,
    step_id: params.stepId,
    step_index: params.stepIndex + 1,
    step_total: params.stepTotal,
    skipped: params.skipped,
    surface: ONBOARDING_SURFACE,
    // The rail only ever runs for an authenticated trialist, but the dimension
    // travels anyway so flow rows from both runs union without a null.
    authenticated: true,
  });
}

/** The rail reached the end. Once per run, in addition to the final step. */
export function trackRailComplete(params: {
  stepsCompleted: number;
}) {
  trackFlowComplete({
    flow_name: ONBOARDING_FLOW_NAME,
    steps_completed: params.stepsCompleted,
    surface: ONBOARDING_SURFACE,
    authenticated: true,
  });
}
